<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import Button from 'dashboard/components-next/button/Button.vue';
import DropIndicator from './DropIndicator.vue';
import AddTaskButton from './AddTaskButton.vue';

const props = defineProps({
  columns: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(['taskMove', 'openCreateDrawer', 'columnEdit']);

const { t } = useI18n();

// État du drag & drop
const draggedTaskId = ref(null);
const activeColumn = ref(null);

// Colonnes triées
const columnEntries = computed(() => Object.entries(props.columns));

// Nettoyer les highlights
const clearHighlights = (els = null) => {
  const indicators =
    els || Array.from(document.querySelectorAll('[data-before]'));
  indicators.forEach(indicator => {
    const i = indicator;
    i.style.opacity = '0';
  });
};

// Obtenir les indicateurs d'une colonne
const getIndicators = columnId => {
  return Array.from(document.querySelectorAll(`[data-column="${columnId}"]`));
};

// Obtenir l'indicateur le plus proche
const getNearestIndicator = (e, indicators) => {
  const DISTANCE_OFFSET = 50;

  const el = indicators.reduce(
    (closest, child) => {
      const box = child.getBoundingClientRect();
      const offset = e.clientY - (box.top + DISTANCE_OFFSET);

      if (offset < 0 && offset > closest.offset) {
        return { offset, element: child };
      }
      return closest;
    },
    {
      offset: Number.NEGATIVE_INFINITY,
      element: indicators[indicators.length - 1],
    }
  );

  return el;
};

// Trouver la colonne d'une tâche
const findTaskColumn = taskId => {
  const entries = Object.entries(props.columns);
  const foundEntry = entries.find(([, column]) =>
    column.tasks.some(taskItem => taskItem.id === taskId)
  );
  return foundEntry ? foundEntry[0] : null;
};

// Highlighter l'indicateur le plus proche
const highlightIndicator = (e, columnId) => {
  const indicators = getIndicators(columnId);
  clearHighlights(indicators);

  const el = getNearestIndicator(e, indicators);
  if (el?.element) {
    el.element.style.opacity = '1';
  }
};

// Démarrer le drag d'une tâche
const handleDragStart = (e, task) => {
  e.dataTransfer.effectAllowed = 'move';
  e.dataTransfer.setData('cardId', task.id);
  draggedTaskId.value = task.id;
};

// Fin du drag
const handleDragEnd = e => {
  const cardId = e.dataTransfer.getData('cardId');
  const column = activeColumn.value;

  if (!column) {
    draggedTaskId.value = null;
    activeColumn.value = null;
    clearHighlights();
    return;
  }

  clearHighlights();

  const indicators = getIndicators(column);
  const { element } = getNearestIndicator(e, indicators);

  const before = element?.dataset?.before || '-1';

  if (before !== cardId) {
    const fromColumn = findTaskColumn(cardId);
    const toColumn = column;

    if (!fromColumn) return;

    let newIndex;
    if (before === '-1') {
      // Déplacer à la fin
      newIndex = props.columns[toColumn].tasks.length;
    } else {
      // Trouver l'index de la carte avant laquelle on insère
      newIndex = props.columns[toColumn].tasks.findIndex(
        taskItem => taskItem.id === before
      );
    }

    // Ajuster l'index si on déplace dans la même colonne
    if (fromColumn === toColumn) {
      const oldIndex = props.columns[fromColumn].tasks.findIndex(
        taskItem => taskItem.id === cardId
      );
      if (newIndex > oldIndex) {
        newIndex -= 1;
      }
    }

    emit('taskMove', {
      taskId: cardId,
      fromColumn,
      toColumn,
      newIndex,
    });
  }

  draggedTaskId.value = null;
  activeColumn.value = null;
};

// Drag over une colonne
const handleDragOver = (e, columnId) => {
  e.preventDefault();
  highlightIndicator(e, columnId);
  activeColumn.value = columnId;
};

// Drag leave
const handleDragLeave = () => {
  clearHighlights();
  activeColumn.value = null;
};
</script>

<template>
  <div class="flex gap-4 h-full pb-6 min-h-0 overflow-x-auto">
    <!-- Colonnes kanban -->
    <div
      v-for="[columnId, column] in columnEntries"
      :key="columnId"
      class="w-80 shrink-0 group"
    >
      <!-- En-tête de colonne avec style amélioré -->
      <div class="mb-4 flex items-center justify-between px-1">
        <div class="flex items-center gap-2.5">
          <div
            class="size-2.5 rounded-full shadow-sm"
            :class="
              column.bgColor && !column.bgColor.startsWith('[')
                ? column.bgColor
                : ''
            "
            :style="
              column.bgColor?.startsWith('[')
                ? column.bgColor
                    .replace('[background-color:', 'background-color:')
                    .replace(']', '')
                : {}
            "
          />
          <h3
            class="font-bold text-sm tracking-tight"
            :class="
              column.color && !column.color.startsWith('[') ? column.color : ''
            "
            :style="
              column.color?.startsWith('[')
                ? column.color.replace('[color:', 'color:').replace(']', '')
                : {}
            "
          >
            {{ column.title }}
          </h3>
          <span
            class="px-2 py-0.5 rounded-full text-[11px] font-semibold bg-n-alpha-2 text-n-slate-11"
          >
            {{ column.tasks.length }}
          </span>
        </div>
        <Button
          icon="i-lucide-settings"
          variant="ghost"
          size="sm"
          color="slate"
          class="!size-7 !p-0 opacity-60 hover:opacity-100 group-hover:opacity-100 transition-opacity"
          @click.stop="emit('columnEdit', column)"
        />
      </div>

      <!-- Zone de drop avec style amélioré -->
      <div
        class="min-h-[400px] p-3 rounded-2xl border-2 border-dashed transition-all duration-300"
        :class="{
          'bg-gradient-to-b from-n-brand/5 to-n-brand/10 border-n-brand/40 shadow-lg shadow-n-brand/10':
            activeColumn === columnId,
          'bg-n-alpha-1/50 border-n-weak/50 hover:border-n-weak':
            activeColumn !== columnId,
        }"
        @drop="handleDragEnd"
        @dragover="e => handleDragOver(e, columnId)"
        @dragleave="handleDragLeave"
      >
        <!-- Tâches -->
        <div class="space-y-3">
          <template v-for="task in column.tasks" :key="task.id">
            <DropIndicator :before-id="task.id" :column="columnId" />
            <div
              draggable="true"
              class="cursor-grab active:cursor-grabbing transition-all duration-200"
              :class="{
                'opacity-30 scale-95': draggedTaskId === task.id,
              }"
              @dragstart="e => handleDragStart(e, task)"
            >
              <slot name="task" :task="task" :column-id="columnId" />
            </div>
          </template>

          <!-- Indicateur de fin -->
          <DropIndicator :before-id="null" :column="columnId" />
        </div>

        <!-- Empty state -->
        <div
          v-if="column.tasks.length === 0"
          class="flex flex-col items-center justify-center py-12 text-center"
        >
          <div
            class="size-16 rounded-full flex items-center justify-center mb-3 bg-n-alpha-2"
            :class="
              column.color && !column.color.startsWith('[') ? column.color : ''
            "
            :style="
              column.color?.startsWith('[')
                ? column.color.replace('[color:', 'color:').replace(']', '')
                : {}
            "
          >
            <span class="i-lucide-inbox size-8" />
          </div>
          <p class="text-xs text-n-slate-10 font-medium">
            {{ t('TASKS.EMPTY_COLUMN') }}
          </p>
        </div>

        <!-- Bouton d'ajout de tâche -->
        <div class="mt-3">
          <AddTaskButton @open-drawer="emit('openCreateDrawer', columnId)" />
        </div>
      </div>
    </div>
  </div>
</template>
